import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import {
    ArrowLeft,
    Plus,
    Calendar as CalendarIcon,
    MoreHorizontal,
    CheckCircle2,
    Circle,
    Clock,
    AlertTriangle,
    GanttChart,
    ListTodo,
    Sparkles,
    Edit,
    Trash2,
    User,
    Users,
    MessageSquare,
} from "lucide-react";
import { cn, getStatusColor, getPriorityColor, statusLabels, priorityLabels, formatDate, formatCurrency } from "../../lib/utils";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { format, differenceInDays, addDays, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { ProjectMembersTab } from "../../components/projects/ProjectMembersTab";

const initialTaskForm = {
    name: "",
    description: "",
    assigned_to: "",
    start_date: null,
    due_date: null,
    estimated_hours: "",
    status: "todo",
    priority: "medium",
};

export const ProjectDetailPage = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const { get, post, put, del } = useApi();
    const { user } = useAuth();
    
    const [project, setProject] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [taskDialogOpen, setTaskDialogOpen] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [taskForm, setTaskForm] = useState(initialTaskForm);
    const [submitting, setSubmitting] = useState(false);
    const [aiSummary, setAiSummary] = useState(null);
    const [loadingAi, setLoadingAi] = useState(false);
    const [projectChannelId, setProjectChannelId] = useState(null);
    
    // Check if user is admin or project owner
    const isAdmin = user?.role === "admin" || user?.role === "tenant_admin";
    const isOwner = project?.created_by === user?.user_id;

    const fetchData = useCallback(async () => {
        const results = await Promise.allSettled([
            get(`/projects/${projectId}`),
            get(`/projects/${projectId}/tasks`),
            get("/employees"),
            get(`/messaging/projects/${projectId}/channel`),
        ]);
        if (results[0].status === "fulfilled") {
            setProject(results[0].value);
        } else {
            toast.error("Erreur lors du chargement du projet");
            navigate("/projects");
            return;
        }
        if (results[1].status === "fulfilled") setTasks(results[1].value);
        if (results[2].status === "fulfilled") setEmployees(results[2].value);
        if (results[3].status === "fulfilled" && results[3].value?.channel) {
            setProjectChannelId(results[3].value.channel.conversation_id);
        }
        setLoading(false);
    }, [get, projectId, navigate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleTaskSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            const payload = {
                ...taskForm,
                project_id: projectId,
                estimated_hours: taskForm.estimated_hours ? parseFloat(taskForm.estimated_hours) : null,
                assigned_to: taskForm.assigned_to || null,
            };

            if (editingTask) {
                await put(`/tasks/${editingTask.task_id}`, payload);
                toast.success("Tâche mise à jour");
            } else {
                await post("/tasks", payload);
                toast.success("Tâche créée");
            }

            setTaskDialogOpen(false);
            setEditingTask(null);
            setTaskForm(initialTaskForm);
            fetchData();
        } catch (error) {
            toast.error("Erreur lors de la sauvegarde");
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditTask = (task) => {
        setEditingTask(task);
        setTaskForm({
            name: task.name,
            description: task.description || "",
            assigned_to: task.assigned_to || "",
            start_date: task.start_date ? new Date(task.start_date) : null,
            due_date: task.due_date ? new Date(task.due_date) : null,
            estimated_hours: task.estimated_hours?.toString() || "",
            status: task.status,
            priority: task.priority,
        });
        setTaskDialogOpen(true);
    };

    const handleDeleteTask = async (taskId) => {
        if (!window.confirm("Supprimer cette tâche ?")) return;
        try {
            await del(`/tasks/${taskId}`);
            toast.success("Tâche supprimée");
            fetchData();
        } catch (error) {
            toast.error("Erreur lors de la suppression");
        }
    };

    const handleStatusChange = async (taskId, newStatus) => {
        const task = tasks.find(t => t.task_id === taskId);
        if (!task) return;

        try {
            await put(`/tasks/${taskId}`, {
                ...task,
                status: newStatus,
            });
            fetchData();
        } catch (error) {
            toast.error("Erreur lors de la mise à jour");
        }
    };

    const generateAiSummary = async () => {
        setLoadingAi(true);
        try {
            const response = await post(`/ai/project-summary/${projectId}`);
            setAiSummary(response.response);
        } catch (error) {
            toast.error("Erreur lors de la génération du résumé IA");
        } finally {
            setLoadingAi(false);
        }
    };

    // Gantt Chart Data
    const ganttData = useMemo(() => {
        if (!tasks.length || !project) return [];

        const projectStart = project.start_date ? new Date(project.start_date) : new Date();
        const projectEnd = project.end_date ? new Date(project.end_date) : addDays(projectStart, 90);
        const totalDays = Math.max(differenceInDays(projectEnd, projectStart), 30);

        return tasks.map(task => {
            const taskStart = task.start_date ? new Date(task.start_date) : projectStart;
            const taskEnd = task.due_date ? new Date(task.due_date) : addDays(taskStart, 7);
            
            const startOffset = Math.max(0, differenceInDays(taskStart, projectStart));
            const duration = Math.max(1, differenceInDays(taskEnd, taskStart));
            
            return {
                ...task,
                startPercent: (startOffset / totalDays) * 100,
                widthPercent: (duration / totalDays) * 100,
                taskStart,
                taskEnd,
            };
        });
    }, [tasks, project]);

    // Task statistics
    const taskStats = useMemo(() => {
        const total = tasks.length;
        const completed = tasks.filter(t => t.status === "completed").length;
        const inProgress = tasks.filter(t => t.status === "in_progress").length;
        const overdue = tasks.filter(t => {
            if (!t.due_date || t.status === "completed") return false;
            return new Date(t.due_date) < new Date();
        }).length;

        return { total, completed, inProgress, overdue };
    }, [tasks]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!project) {
        return null;
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="project-detail-page">
            {/* Header */}
            <div className="flex flex-col gap-4">
                <Button variant="ghost" onClick={() => navigate("/projects")} className="w-fit">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Retour aux projets
                </Button>

                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <h1 className="font-heading font-bold text-2xl sm:text-3xl">{project.name}</h1>
                            <Badge className={getStatusColor(project.status)}>
                                {statusLabels[project.status]}
                            </Badge>
                            <Badge className={getPriorityColor(project.priority)}>
                                {priorityLabels[project.priority]}
                            </Badge>
                        </div>
                        {project.client_name && (
                            <p className="text-muted-foreground">Client: {project.client_name}</p>
                        )}
                        {project.description && (
                            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{project.description}</p>
                        )}
                    </div>

                    <div className="flex gap-2">
                        {projectChannelId && (
                            <Button
                                variant="outline"
                                onClick={() => navigate(`/messaging?conversation=${projectChannelId}`)}
                                data-testid="project-chat-btn"
                            >
                                <MessageSquare className="w-4 h-4 mr-2" />
                                Chat du projet
                            </Button>
                        )}
                        <Button variant="outline" onClick={generateAiSummary} disabled={loadingAi}>
                            <Sparkles className="w-4 h-4 mr-2" />
                            {loadingAi ? "Génération..." : "Résumé IA"}
                        </Button>
                        <Dialog open={taskDialogOpen} onOpenChange={(open) => {
                            setTaskDialogOpen(open);
                            if (!open) {
                                setEditingTask(null);
                                setTaskForm(initialTaskForm);
                            }
                        }}>
                            <DialogTrigger asChild>
                                <Button data-testid="add-task-btn">
                                    <Plus className="w-4 h-4 mr-2" />
                                    Nouvelle tâche
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-lg">
                                <DialogHeader>
                                    <DialogTitle className="font-heading">
                                        {editingTask ? "Modifier la tâche" : "Nouvelle tâche"}
                                    </DialogTitle>
                                </DialogHeader>
                                <form onSubmit={handleTaskSubmit} className="space-y-4">
                                    <div>
                                        <Label>Nom de la tâche *</Label>
                                        <Input
                                            value={taskForm.name}
                                            onChange={(e) => setTaskForm({ ...taskForm, name: e.target.value })}
                                            required
                                            data-testid="task-name-input"
                                        />
                                    </div>

                                    <div>
                                        <Label>Description</Label>
                                        <Textarea
                                            value={taskForm.description}
                                            onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                                            rows={2}
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label>Assigné à</Label>
                                            <Select
                                                value={taskForm.assigned_to}
                                                onValueChange={(value) => setTaskForm({ ...taskForm, assigned_to: value })}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Sélectionner..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {employees.map((emp) => (
                                                        <SelectItem key={emp.employee_id} value={emp.employee_id}>
                                                            {emp.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <div>
                                            <Label>Heures estimées</Label>
                                            <Input
                                                type="number"
                                                value={taskForm.estimated_hours}
                                                onChange={(e) => setTaskForm({ ...taskForm, estimated_hours: e.target.value })}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label>Date de début</Label>
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <Button variant="outline" className={cn("w-full justify-start", !taskForm.start_date && "text-muted-foreground")}>
                                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                                        {taskForm.start_date ? format(taskForm.start_date, "dd/MM/yyyy") : "Sélectionner"}
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-auto p-0">
                                                    <Calendar
                                                        mode="single"
                                                        selected={taskForm.start_date}
                                                        onSelect={(date) => setTaskForm({ ...taskForm, start_date: date })}
                                                    />
                                                </PopoverContent>
                                            </Popover>
                                        </div>

                                        <div>
                                            <Label>Date d'échéance</Label>
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <Button variant="outline" className={cn("w-full justify-start", !taskForm.due_date && "text-muted-foreground")}>
                                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                                        {taskForm.due_date ? format(taskForm.due_date, "dd/MM/yyyy") : "Sélectionner"}
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-auto p-0">
                                                    <Calendar
                                                        mode="single"
                                                        selected={taskForm.due_date}
                                                        onSelect={(date) => setTaskForm({ ...taskForm, due_date: date })}
                                                    />
                                                </PopoverContent>
                                            </Popover>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label>Statut</Label>
                                            <Select
                                                value={taskForm.status}
                                                onValueChange={(value) => setTaskForm({ ...taskForm, status: value })}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="todo">À faire</SelectItem>
                                                    <SelectItem value="in_progress">En cours</SelectItem>
                                                    <SelectItem value="review">En revue</SelectItem>
                                                    <SelectItem value="completed">Terminé</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <div>
                                            <Label>Priorité</Label>
                                            <Select
                                                value={taskForm.priority}
                                                onValueChange={(value) => setTaskForm({ ...taskForm, priority: value })}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="low">Basse</SelectItem>
                                                    <SelectItem value="medium">Moyenne</SelectItem>
                                                    <SelectItem value="high">Haute</SelectItem>
                                                    <SelectItem value="critical">Critique</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>

                                    <div className="flex justify-end gap-2">
                                        <Button type="button" variant="outline" onClick={() => setTaskDialogOpen(false)}>
                                            Annuler
                                        </Button>
                                        <Button type="submit" disabled={submitting} data-testid="submit-task-btn">
                                            {submitting ? "Sauvegarde..." : editingTask ? "Mettre à jour" : "Créer"}
                                        </Button>
                                    </div>
                                </form>
                            </DialogContent>
                        </Dialog>
                    </div>
                </div>
            </div>

            {/* AI Summary */}
            {aiSummary && (
                <Card className="border-primary/20 bg-primary/5">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-primary" />
                            Résumé IA du projet
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-sm whitespace-pre-wrap">{aiSummary}</p>
                    </CardContent>
                </Card>
            )}

            {/* Project Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                                <ListTodo className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold font-mono">{taskStats.total}</p>
                                <p className="text-xs text-muted-foreground">Tâches totales</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                                <CheckCircle2 className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold font-mono">{taskStats.completed}</p>
                                <p className="text-xs text-muted-foreground">Terminées</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/30">
                                <Clock className="w-5 h-5 text-yellow-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold font-mono">{taskStats.inProgress}</p>
                                <p className="text-xs text-muted-foreground">En cours</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                                <AlertTriangle className="w-5 h-5 text-red-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold font-mono">{taskStats.overdue}</p>
                                <p className="text-xs text-muted-foreground">En retard</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Project Info Bar */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="flex items-center gap-6">
                            <div>
                                <p className="text-xs text-muted-foreground">Progression</p>
                                <div className="flex items-center gap-2 mt-1">
                                    <Progress value={project.progress} className="w-32 h-2" />
                                    <span className="font-mono font-medium">{project.progress}%</span>
                                </div>
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground">Budget</p>
                                <p className="font-mono font-medium">{project.budget ? formatCurrency(project.budget) : "-"}</p>
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground">Période</p>
                                <p className="font-medium">
                                    {formatDate(project.start_date)} → {formatDate(project.end_date)}
                                </p>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Tabs: Tasks List & Gantt */}
            <Tabs defaultValue="list" className="space-y-4">
                <TabsList>
                    <TabsTrigger value="list" data-testid="tab-list">
                        <ListTodo className="w-4 h-4 mr-2" />
                        Liste des tâches
                    </TabsTrigger>
                    <TabsTrigger value="gantt" data-testid="tab-gantt">
                        <GanttChart className="w-4 h-4 mr-2" />
                        Diagramme de Gantt
                    </TabsTrigger>
                    <TabsTrigger value="members" data-testid="tab-members">
                        <Users className="w-4 h-4 mr-2" />
                        Membres
                    </TabsTrigger>
                </TabsList>

                {/* Tasks List View */}
                <TabsContent value="list" className="space-y-4">
                    {tasks.length === 0 ? (
                        <Card>
                            <CardContent className="py-12 text-center text-muted-foreground">
                                <ListTodo className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>Aucune tâche pour ce projet</p>
                                <Button className="mt-4" onClick={() => setTaskDialogOpen(true)}>
                                    <Plus className="w-4 h-4 mr-2" />
                                    Créer la première tâche
                                </Button>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="space-y-2">
                            {tasks.map((task) => {
                                const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== "completed";
                                const assignee = employees.find(e => e.employee_id === task.assigned_to);

                                return (
                                    <Card
                                        key={task.task_id}
                                        className={cn(
                                            "transition-all hover:shadow-md",
                                            task.status === "completed" && "opacity-60"
                                        )}
                                        data-testid={`task-card-${task.task_id}`}
                                    >
                                        <CardContent className="p-4">
                                            <div className="flex items-start gap-4">
                                                <Checkbox
                                                    checked={task.status === "completed"}
                                                    onCheckedChange={(checked) => 
                                                        handleStatusChange(task.task_id, checked ? "completed" : "todo")
                                                    }
                                                    className="mt-1"
                                                />

                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-start justify-between gap-2">
                                                        <div>
                                                            <h4 className={cn(
                                                                "font-medium",
                                                                task.status === "completed" && "line-through"
                                                            )}>
                                                                {task.name}
                                                            </h4>
                                                            {task.description && (
                                                                <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                                                                    {task.description}
                                                                </p>
                                                            )}
                                                        </div>

                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                                                    <MoreHorizontal className="w-4 h-4" />
                                                                </Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent align="end">
                                                                <DropdownMenuItem onClick={() => handleEditTask(task)}>
                                                                    <Edit className="w-4 h-4 mr-2" />
                                                                    Modifier
                                                                </DropdownMenuItem>
                                                                <DropdownMenuItem
                                                                    className="text-destructive"
                                                                    onClick={() => handleDeleteTask(task.task_id)}
                                                                >
                                                                    <Trash2 className="w-4 h-4 mr-2" />
                                                                    Supprimer
                                                                </DropdownMenuItem>
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    </div>

                                                    <div className="flex flex-wrap items-center gap-2 mt-3">
                                                        <Badge className={getStatusColor(task.status)}>
                                                            {statusLabels[task.status]}
                                                        </Badge>
                                                        <Badge className={getPriorityColor(task.priority)}>
                                                            {priorityLabels[task.priority]}
                                                        </Badge>

                                                        {task.due_date && (
                                                            <span className={cn(
                                                                "text-xs flex items-center gap-1",
                                                                isOverdue ? "text-red-600" : "text-muted-foreground"
                                                            )}>
                                                                <CalendarIcon className="w-3 h-3" />
                                                                {formatDate(task.due_date)}
                                                                {isOverdue && " (En retard)"}
                                                            </span>
                                                        )}

                                                        {assignee && (
                                                            <span className="text-xs flex items-center gap-1 text-muted-foreground">
                                                                <User className="w-3 h-3" />
                                                                {assignee.name}
                                                            </span>
                                                        )}

                                                        {task.estimated_hours && (
                                                            <span className="text-xs text-muted-foreground">
                                                                {task.estimated_hours}h estimées
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </TabsContent>

                {/* Gantt Chart View */}
                <TabsContent value="gantt">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg">Diagramme de Gantt</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {ganttData.length === 0 ? (
                                <div className="text-center py-12 text-muted-foreground">
                                    <GanttChart className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                    <p>Ajoutez des tâches avec des dates pour voir le diagramme</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {/* Timeline header */}
                                    <div className="flex items-center border-b pb-2 mb-4">
                                        <div className="w-48 flex-shrink-0 font-medium text-sm">Tâche</div>
                                        <div className="flex-1 relative h-6">
                                            <div className="absolute left-0 text-xs text-muted-foreground">
                                                {project.start_date ? formatDate(project.start_date) : "Début"}
                                            </div>
                                            <div className="absolute right-0 text-xs text-muted-foreground">
                                                {project.end_date ? formatDate(project.end_date) : "Fin"}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Task bars */}
                                    {ganttData.map((task) => {
                                        const statusColors = {
                                            todo: "bg-gray-400",
                                            in_progress: "bg-blue-500",
                                            review: "bg-purple-500",
                                            completed: "bg-green-500",
                                        };

                                        return (
                                            <div key={task.task_id} className="flex items-center py-2 hover:bg-secondary/50 rounded">
                                                <div className="w-48 flex-shrink-0 pr-4">
                                                    <p className="text-sm font-medium truncate">{task.name}</p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {formatDate(task.taskStart)} - {formatDate(task.taskEnd)}
                                                    </p>
                                                </div>
                                                <div className="flex-1 relative h-8 bg-secondary/30 rounded">
                                                    <div
                                                        className={cn(
                                                            "absolute h-6 top-1 rounded transition-all",
                                                            statusColors[task.status]
                                                        )}
                                                        style={{
                                                            left: `${Math.min(task.startPercent, 100)}%`,
                                                            width: `${Math.min(task.widthPercent, 100 - task.startPercent)}%`,
                                                            minWidth: "4px",
                                                        }}
                                                    >
                                                        <span className="absolute inset-0 flex items-center justify-center text-xs text-white font-medium truncate px-1">
                                                            {task.widthPercent > 15 && task.name}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Members Tab */}
                <TabsContent value="members">
                    <Card>
                        <CardContent className="pt-6">
                            <ProjectMembersTab 
                                projectId={projectId} 
                                isOwner={isOwner} 
                                isAdmin={isAdmin}
                            />
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
};
